import React, { useState, useEffect } from 'react';
import { Box, styled, Drawer, useMediaQuery, Alert, Collapse, Button } from '@mui/material';
import { useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { isPathAllowed } from '../../config/moduleAccess';
import Sidebar from './Sidebar';
import Header from './Header';
import MobileMenu from './MobileMenu';
import TabBar from './TabBar';
import TabContent from './TabContent';
import DemoExpiredScreen from './DemoExpiredScreen';
import { useTabContextSafe } from '../../contexts/TabContext';
import { isTabManagedRoute } from './routeComponentMap';
import DueRemindersNotifier from '../common/DueRemindersNotifier';
import { DueRemindersProvider } from '../../contexts/DueRemindersContext';
import AiChatFab from '../aichat/AiChatFab';
import BrowserChromeTint from '../common/BrowserChromeTint';
import LicensePaymentDialog from '../billing/LicensePaymentDialog';

const MainContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  minHeight: '100vh',
  backgroundColor: '#f5f5f5',
}));

const HEADER_HEIGHT = 56;
const TAB_BAR_HEIGHT = 40;

const ContentContainer = styled(Box)<{ isCollapsed: boolean; hasTabBar: boolean }>(({ theme, isCollapsed, hasTabBar }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  paddingTop: hasTabBar ? HEADER_HEIGHT + TAB_BAR_HEIGHT + 16 : HEADER_HEIGHT + 24,
  marginLeft: isCollapsed ? 70 : 260,
  width: `calc(100% - ${isCollapsed ? 70 : 260}px)`,
  height: '100vh',
  overflow: 'auto',
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  '@media (max-width: 1399px)': {
    marginLeft: 0,
    width: '100%',
  },
  '@media (max-width: 999px)': {
    paddingTop: HEADER_HEIGHT + 24,
  },
}));

interface MainLayoutProps {
  children: React.ReactNode;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const isMobile = useMediaQuery('(max-width: 1399px)');
  const isDesktopTabs = useMediaQuery('(min-width: 1000px)');
  const tabContext = useTabContextSafe();
  const location = useLocation();
  const { enabledModules, demoUntil, demoExpired, licenseUntil } = useAuth();
  const currentPathIsTabManaged = isTabManagedRoute(location.pathname);

  // Ödeme popup'ı (banttaki "Ödeme yap" butonundan açılır).
  const [licenseDialogOpen, setLicenseDialogOpen] = useState(false);

  // Demo uyarı bandı: kalan gün ≤ 7 ise gösterilir; oturum boyunca kapatılabilir.
  const [demoBannerDismissed, setDemoBannerDismissed] = useState(false);
  const demoDaysRemaining = React.useMemo(() => {
    if (!demoUntil || demoExpired) return null;
    const diffMs = new Date(demoUntil).getTime() - Date.now();
    return Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
  }, [demoUntil, demoExpired]);
  const showDemoBanner =
    !demoBannerDismissed && demoDaysRemaining !== null && demoDaysRemaining <= 7;

  // Lisans uyarı bandı: demo değilken lisans (sözleşme) bitişine ≤7 gün kalınca.
  const [licenseBannerDismissed, setLicenseBannerDismissed] = useState(false);
  const licenseDaysRemaining = React.useMemo(() => {
    if (demoUntil || !licenseUntil) return null; // demo aktifken lisans bandı yok
    const diffMs = new Date(licenseUntil).getTime() - Date.now();
    return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  }, [demoUntil, licenseUntil]);
  const showLicenseBanner =
    !licenseBannerDismissed && licenseDaysRemaining !== null && licenseDaysRemaining <= 7;
  
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleCollapse = (collapsed: boolean) => {
    setIsCollapsed(collapsed);
  };
  
  useEffect(() => {
    const handleOpenMobileMenu = () => {
      if (isMobile) {
        setMobileOpen(true);
      }
    };

    window.addEventListener('openMobileMenu', handleOpenMobileMenu);

    return () => {
      window.removeEventListener('openMobileMenu', handleOpenMobileMenu);
    };
  }, [isMobile]);

  const showTabBar = isDesktopTabs && tabContext !== null;
  const useTabContent = showTabBar && currentPathIsTabManaged;

  // Modül koruması (SuperAdmin modül kontrolü): kapalı modülün rotasına URL ile
  // girilirse serbest bir sayfaya yönlendir. Menü zaten gizli; bu, doğrudan URL
  // yazan kullanıcıyı da engeller. enabledModules=null iken her yol serbesttir.
  // Demo süresi dolduysa yönlendirme yapılmaz — aşağıda hangi yolda olursa olsun
  // kilit ekranı render edilir.
  if (!demoExpired && !isPathAllowed(location.pathname, enabledModules)) {
    return <Navigate to="/hizli-baslangic" replace />;
  }

  return (
    // Vade hatırlatmaları: Header'daki bildirim zili ile sağ alttaki uyarı bandı
    // aynı veriyi paylaşır (tek istek).
    <DueRemindersProvider>
    <MainContainer>
      {/* Tarayıcı arayüzü, kullanıcının seçtiği kenar çubuğu rengini alsın */}
      <BrowserChromeTint color="var(--sidebar-color, #102648)" />

      {/* Desktop Sidebar */}
      {!isMobile && (
        <Sidebar onCollapse={handleCollapse} />
      )}
      
      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          '@media (max-width: 1399px)': { display: 'block' },
          '@media (min-width: 1400px)': { display: 'none' },
          '& .MuiDrawer-paper': { width: 280, boxSizing: 'border-box' },
        }}
      >
        <MobileMenu onClose={handleDrawerToggle} />
      </Drawer>
      
      {/* Header */}
      <Header onToggleSidebar={handleDrawerToggle} isCollapsed={isCollapsed} />
      
      {/* Tab Bar - Desktop only */}
      {showTabBar && (
        <TabBar isCollapsed={isCollapsed} />
      )}
      
      {/* Main Content */}
      <ContentContainer isCollapsed={isCollapsed} hasTabBar={showTabBar}>
        {/* Demo uyarı bandı (kalan gün ≤ 7, kapatılabilir) — "Ödeme yap" ile popup açılır */}
        {showDemoBanner && (
          <Collapse in={showDemoBanner}>
            <Alert
              severity="warning"
              onClose={() => setDemoBannerDismissed(true)}
              action={
                <Button color="inherit" size="small" onClick={() => setLicenseDialogOpen(true)}>
                  Ödeme yap
                </Button>
              }
              sx={{ mb: 2 }}
            >
              {demoDaysRemaining === 0
                ? 'Demo süreniz bugün doluyor! Lisanslı ürüne geçmek için ödemenizi tamamlayın.'
                : `Demo sürenizin bitmesine ${demoDaysRemaining} gün kaldı. Lisanslı ürüne geçmek için ödemenizi tamamlayın.`}
            </Alert>
          </Collapse>
        )}
        {/* Lisans uyarı bandı (demo değilken, bitişe ≤ 7 gün, kapatılabilir) */}
        {showLicenseBanner && (
          <Collapse in={showLicenseBanner}>
            <Alert
              severity={licenseDaysRemaining !== null && licenseDaysRemaining < 0 ? 'error' : 'warning'}
              onClose={() => setLicenseBannerDismissed(true)}
              action={
                <Button color="inherit" size="small" onClick={() => setLicenseDialogOpen(true)}>
                  Ödeme yap
                </Button>
              }
              sx={{ mb: 2 }}
            >
              {licenseDaysRemaining !== null && licenseDaysRemaining < 0
                ? 'Lisans süreniz doldu! Kesintisiz kullanım için ödemenizi tamamlayın.'
                : licenseDaysRemaining === 0
                  ? 'Lisans süreniz bugün doluyor! Kesintisiz kullanım için ödemenizi tamamlayın.'
                  : `Lisans sürenizin bitmesine ${licenseDaysRemaining} gün kaldı. Kesintisiz kullanım için ödemenizi tamamlayın.`}
            </Alert>
          </Collapse>
        )}
        {/* Demo süresi doldu: içerik yerine kilit ekranı (sunucu kararı — demoExpired) */}
        {demoExpired ? (
          <DemoExpiredScreen />
        ) : useTabContent ? (
          <TabContent />
        ) : (
          children
        )}
      </ContentContainer>

      {/* Vade hatırlatmaları: kredi, kredi kartı, çek/senet (uygulama geneli) */}
      <DueRemindersNotifier />

      {/* AI chat asistanı (şirket bayrağı açıksa tüm yönetici/muhasebe sayfalarında) */}
      {!demoExpired && <AiChatFab />}

      {/* Lisans / ödeme popup'ı (uyarı bandındaki "Ödeme yap" butonundan açılır) */}
      <LicensePaymentDialog
        open={licenseDialogOpen}
        onClose={() => setLicenseDialogOpen(false)}
      />
    </MainContainer>
    </DueRemindersProvider>
  );
};

export default MainLayout; 